import { type Ref } from 'vue';
import { airflow } from '@aviata/platform-client';
import type { Dict } from '@aviata/platform-types';
import {
  citySearchHistory,
  type searchForm as searchFormState,
  type MappedLocation,
} from 'shared';

import { lastSearchModel } from '@/entities/last-search';
import {
  recentSearchesLib,
  recentSearchesModel,
} from '@/entities/recent-searches';

import { sentryModel } from '@/shared/model';
import { analyticsEventsLib } from '@/shared/lib';
import type { MainPageVariant } from '@/shared/lib';

import { MT_SESSION_KEY } from '../config';

export type AviaSearchFormEmit = (
  event: 'onSearchFormSubmit',
  searchParams: string,
  query?: string,
) => void;

type MultiCityForm = ReturnType<
  typeof searchFormState.model.useMultiCitySearchForm
>;
type SearchPayload = NonNullable<
  ReturnType<MultiCityForm['mapSearchFormPayload']>
>;

/**
 * Submit pipeline for the avia search form.
 *
 * Knows nothing about *how* validation is performed — the orchestrator passes
 * a single `validate` callback returning either `null` (valid) or a localised
 * error message. Same for `showError`. This keeps the pipeline a pure linear
 * sequence: validate → build payload → persist → analytics → emit.
 */
export function useAviaSearchSubmit(deps: {
  searchForm: MultiCityForm;
  selectedAirline: Ref<Dict.Airlines.Airline | null>;
  validate: () => string | null;
  showError: (message: string) => void;
  emit: AviaSearchFormEmit;
  getSource: () => string;
  getVariant?: () => MainPageVariant | undefined;
}) {
  const {
    searchForm,
    selectedAirline,
    validate,
    showError,
    emit,
    getSource,
    getVariant,
  } = deps;

  // ---------- Pipeline steps ----------

  const persistSearch = (payload: SearchPayload) => {
    const snapshot = {
      routes: searchForm.routes.value,
      passengers: payload.passengers,
      cabinClass: searchForm.cabinClass.value,
      isYouth: searchForm.isYouth.value,
    };
    lastSearchModel.lastSearch.saveSearchParams(snapshot);

    try {
      if (snapshot.routes.length > 1) {
        sessionStorage.setItem(MT_SESSION_KEY, '1');
      } else {
        sessionStorage.removeItem(MT_SESSION_KEY);
      }
    } catch {
      // eslint-disable-next-line no-console
    }

    recentSearchesModel.recentSearches.saveRecentSearch({
      ...snapshot,
      tripPattern: recentSearchesLib.deriveRecentSearchTripPattern(
        searchForm.routes.value,
      ),
    });

    const searchHistory =
      citySearchHistory.model.useCitySearchHistory('free-avia');
    for (const legRoute of searchForm.routes.value) {
      if (legRoute.dep?.code) {
        searchHistory.saveCity(legRoute.dep as MappedLocation);
      }
      if (legRoute.arr?.code) {
        searchHistory.saveCity(legRoute.arr as MappedLocation);
      }
    }
  };

  const reportMissingPayload = () => {
    sentryModel.captureException(
      sentryModel.getReadableError(
        'Search form submission failure: search params is undefined',
        new Error('Search params is undefined'),
      ),
    );
  };

  const emitResult = (payload: SearchPayload) => {
    emit(
      'onSearchFormSubmit',
      airflow.utils.buildQuery(payload),
      selectedAirline.value?.iata,
    );
  };

  // ---------- Public entry-point ----------

  const submitForm = () => {
    const error = validate();
    if (error) {
      showError(error);
      return;
    }

    const payload = searchForm.mapSearchFormPayload();
    if (!payload) {
      reportMissingPayload();
      return;
    }

    persistSearch(payload);
    analyticsEventsLib.submitSearchForm(
      searchForm,
      getSource(),
      getVariant?.(),
    );
    emitResult(payload);
  };

  return { submitForm };
}
